import { FormEvent, useState } from 'react';
import { useDialCode } from './useDialCode';
import { membershipTiers } from '../data/memberships';
import { saveSubmission, SUBMISSION_ERROR } from '../utils/submissions';

type Status = 'idle' | 'sending' | 'sent';

// One @, a name before it, and a domain with a real ending (e.g. .com, .ae)
const EMAIL_RE = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*\.[A-Za-z]{2,}$/;
type Field = 'name' | 'email' | 'phone' | 'city' | 'consent';

export function useApplicationForm() {
  const [tier, setTier] = useState(membershipTiers[0].name);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [dialCode, setDialCode] = useDialCode();
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [referral, setReferral] = useState('');
  const [referrer, setReferrer] = useState('');
  const [interests, setInterests] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  const [submitError, setSubmitError] = useState('');

  const clear = (field: Field) => setErrors((e) => (e[field] ? { ...e, [field]: undefined } : e));

  const emailValid = (v: string) => EMAIL_RE.test(v.trim());
  const checkEmail = () => {
    if (email.trim() && !emailValid(email)) setErrors((e) => ({ ...e, email: 'Please enter a valid email address, e.g. name@domain.com.' }));
  };

  const toggleInterest = (item: string) =>
    setInterests((list) => (list.includes(item) ? list.filter((i) => i !== item) : [...list, item]));

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    const next: Partial<Record<Field, string>> = {};
    if (name.trim().length < 2) next.name = 'Please share your name.';
    if (!emailValid(email)) next.email = 'Please enter a valid email address, e.g. name@domain.com.';
    if (phone.replace(/\D/g, '').length < 6) next.phone = 'Please leave a number we may call.';
    if (city.trim().length < 2) next.city = 'Where do you spend most of your time?';
    if (!consent) next.consent = 'required';
    setErrors(next);
    if (Object.keys(next).length > 0) {
      const first = Object.keys(next)[0];
      document.getElementById(`apply-${first}`)?.focus();
      return;
    }
    setStatus('sending');
    const ok = await saveSubmission({
      source: 'membership_application',
      name,
      email,
      phone: `${dialCode} ${phone}`,
      details: { tier, city, referral, referrer, interests, note, consent: true },
    });
    if (ok) {
      setStatus('sent');
    } else {
      setSubmitError(SUBMISSION_ERROR);
      setStatus('idle');
    }
  };

  return {
    tier, setTier,
    name, setName,
    email, setEmail, checkEmail,
    dialCode, setDialCode,
    phone, setPhone,
    city, setCity,
    referral, setReferral,
    referrer, setReferrer,
    interests, toggleInterest,
    note, setNote,
    consent, setConsent,
    errors, clear,
    status, submit, submitError,
  };
}
